import { useMutation } from "@tanstack/react-query";
import AlertTriggersApi from "api/AlertTriggersApi";
import {
  easasErrorAlert,
  easasSuccessAlert,
} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { queryClient } from "services/HttpService";
import { T } from "services/lang/LanguageService";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import AlertRulesApi from "../AlertRulesApi";
import {
  AlertRuleDTO,
  AlertTriggerDTO,
  CreateAlertRuleRequest,
  CreateStandaloneTriggerRequest,
  UpdateAlertRuleRequest,
} from "../model/alerts/alert-types";
import {
  defaultMutationOptions,
  defaultResponseHandler,
  IMutationOptions,
  IQueryOptions,
  QueryKey,
  useQueryWrapper,
} from "./api-hooks-common";

export const useGetAlertRules = (options?: IQueryOptions<AlertRuleDTO[]>) => {
  const company = useCurrentCompanyContext();
  return useQueryWrapper(
    [QueryKey.ALERT_RULES_KEY],
    () => AlertRulesApi.getAlertRulesForCompany(company?.companyReference ?? ""),
    null,
    {
      staleTime: 1000 * 60,
      ...options,
    }
  );
};

export const useGetAlertRule = (ruleId: number, options?: IQueryOptions<AlertRuleDTO>) =>
  useQueryWrapper(
    [QueryKey.ALERT_RULES_KEY, ruleId.toString()],
    () => AlertRulesApi.getAlertRule(ruleId),
    null,
    options
  );

export const useCreateAlertRule = (specOptions?: IMutationOptions) => {
  const options = { ...defaultMutationOptions, ...specOptions };
  return useMutation({
    mutationFn: async (rule: CreateAlertRuleRequest) => {
      const res = await AlertRulesApi.createAlertRule(rule);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.ALERT_RULES_KEY], (oldData: AlertRuleDTO[] | undefined) =>
        oldData ? [...oldData, data] : [data]
      );
      queryClient.invalidateQueries({ queryKey: [QueryKey.ALERT_TRIGGERS_KEY] });
      easasSuccessAlert(T("alerts.rule-created"));
      options.onSuccess?.(data);
    },
    onError: error => {
      easasErrorAlert(`${T("alerts.rule-create-failed")}: ${error.message}`);
      options.onFailure?.(error);
    },
  });
};

export const useUpdateAlertRule = (specOptions?: IMutationOptions) => {
  const options = { ...defaultMutationOptions, ...specOptions };
  return useMutation({
    mutationFn: async (rule: UpdateAlertRuleRequest) => {
      const res = await AlertRulesApi.updateAlertRule(rule);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.ALERT_TRIGGERS_KEY] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.ALERT_RULES_KEY] });

      queryClient.setQueryData([QueryKey.ALERT_RULES_KEY, data?.id.toString()], data);
      easasSuccessAlert(T("alerts.rule-updated"));
      options.onSuccess?.(data);
    },
    onError: error => {
      easasErrorAlert(`${T("alerts.rule-update-failed")}: ${error.message}`);
      options.onFailure?.(error);
    },
  });
};

export const useDeleteAlertRule = (specOptions?: IMutationOptions) => {
  const options = { ...defaultMutationOptions, ...specOptions };
  return useMutation({
    mutationFn: async (ruleId: number) => {
      const res = await AlertRulesApi.deleteAlertRule(ruleId);
      return defaultResponseHandler(res);
    },
    onSuccess: (_, ruleId) => {
      queryClient.setQueryData([QueryKey.ALERT_RULES_KEY], (oldData: AlertRuleDTO[] | undefined) =>
        oldData?.filter(rule => rule.id !== ruleId)
      );
      easasSuccessAlert(T("alerts.rule-deleted"));
      options.onSuccess?.(ruleId);
    },
    onError: error => {
      easasErrorAlert(`${T("alerts.rule-delete-failed")}: ${error.message}`);
      options.onFailure?.(error);
    },
  });
};

export const useToggleAlertRule = () => {
  return useMutation({
    mutationFn: async ({ ruleId, enabled }: { ruleId: number; enabled: boolean }) => {
      const res = await AlertRulesApi.toggleAlertRule(ruleId, enabled);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.ALERT_RULES_KEY], (oldData: AlertRuleDTO[] | undefined) =>
        oldData?.map(rule => (rule.id === data?.id ? data : rule))
      );
      easasSuccessAlert(data?.enabled ? T("alerts.rule-enabled") : T("alerts.rule-disabled"));
    },
    onError: error => {
      easasErrorAlert(`${T("alerts.rule-toggle-failed")}: ${error.message}`);
    },
  });
};

// ============== Alert Triggers Hooks ==============

export const useGetAllTriggers = (options?: IQueryOptions<AlertTriggerDTO[]>) => {
  const company = useCurrentCompanyContext();
  return useQueryWrapper(
    [QueryKey.ALERT_TRIGGERS_KEY],
    () => AlertTriggersApi.getAllTriggers(company?.companyReference ?? ""),
    null,
    {
      staleTime: 1000 * 60,
      ...options,
    }
  );
};

export const useGetTrigger = (triggerId: number, options?: IQueryOptions<AlertTriggerDTO>) =>
  useQueryWrapper(
    [QueryKey.ALERT_TRIGGERS_KEY, triggerId.toString()],
    () => AlertTriggersApi.getTrigger(triggerId),
    null,
    options
  );

export const useCreateTrigger = (specOptions?: IMutationOptions) => {
  const options = { ...defaultMutationOptions, ...specOptions };
  return useMutation({
    mutationFn: async (trigger: CreateStandaloneTriggerRequest) => {
      const res = await AlertTriggersApi.createTrigger(trigger);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.ALERT_TRIGGERS_KEY], (oldData: AlertTriggerDTO[] | undefined) =>
        oldData ? [...oldData, data] : [data]
      );
      easasSuccessAlert(T("alerts.trigger-created"));
      options.onSuccess?.(data);
    },
    onError: error => {
      easasErrorAlert(`${T("alerts.trigger-create-failed")}: ${error.message}`);
      options.onFailure?.(error);
    },
  });
};

export const useUpdateTrigger = (specOptions?: IMutationOptions) => {
  const options = { ...defaultMutationOptions, ...specOptions };
  return useMutation({
    mutationFn: async ({
      triggerId,
      trigger,
    }: {
      triggerId: number;
      trigger: CreateStandaloneTriggerRequest;
    }) => {
      const res = await AlertTriggersApi.updateTrigger(triggerId, trigger);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.ALERT_TRIGGERS_KEY], (oldData: AlertTriggerDTO[] | undefined) =>
        oldData?.map(t => (t.id === data?.id ? data : t))
      );
      queryClient.setQueryData([QueryKey.ALERT_TRIGGERS_KEY, data?.id?.toString()], data);
      // Also invalidate rules since trigger data may have changed
      queryClient.invalidateQueries({ queryKey: [QueryKey.ALERT_RULES_KEY] });
      easasSuccessAlert(T("alerts.trigger-updated"));
      options.onSuccess?.(data);
    },
    onError: error => {
      easasErrorAlert(`${T("alerts.trigger-update-failed")}: ${error.message}`);
      options.onFailure?.(error);
    },
  });
};

export const useDeleteTrigger = (specOptions?: IMutationOptions) => {
  const options = { ...defaultMutationOptions, ...specOptions };
  return useMutation({
    mutationFn: async (triggerId: number) => {
      const res = await AlertTriggersApi.deleteTrigger(triggerId);
      return defaultResponseHandler(res);
    },
    onSuccess: (_, triggerId) => {
      queryClient.setQueryData([QueryKey.ALERT_TRIGGERS_KEY], (oldData: AlertTriggerDTO[] | undefined) =>
        oldData?.filter(t => t.id !== triggerId)
      );
      easasSuccessAlert(T("alerts.trigger-deleted"));
      options.onSuccess?.(triggerId);
    },
    onError: error => {
      easasErrorAlert(`${T("alerts.trigger-delete-failed")}: ${error.message}`);
      options.onFailure?.(error);
    },
  });
};
